import * as d3 from 'd3';
import { isValid } from '../chartUtils';
import { Position } from '@blueprintjs/core';
import { formatValueScientificNotation, fitLabelWidth } from 'widgets/chartUtils';
import { readableColor } from 'polished';
import { onClear, onMouseOut, onMouseOver } from 'widgets/stackedBarChart/utils';
import { wrappedBand } from './wrappedBand';

// the max length to allow for the labels
const maxLength = 110;

// overall margins for the main chart
const margin = {
	top: 7,
	right: 30,
	bottom: 25,
	left: maxLength,
};

const measureAxisText = (svg) => {
	let width = 0;
	let height = 0;

	d3.select(svg)
		.select('.yAxis')
		.selectAll('.tick')
		.each(function () {
			width = Math.ceil(Math.max(width, this.getBBox().width));
			height = this.getBBox().height;
		});
	return {width, height};
};

const getMarginLeft = (widgetWidth, maxText, limitLength) => {
	return limitLength ? Math.min(/*margin.left*/widgetWidth * .333, maxText) : maxText;
};

/**
 * Creates a horizontal bar chart (bars are wide).
 *
 * @param {*} svg
 * @param updateTooltip
 * @returns {{update: update}}
 * @constructor
 */
export const HorizontalBarChart = ({ svg, updateTooltip, navigateTo }) => {
	let initialized = false;

	// x scale for main chart
	const xScale = d3.scaleLinear();
	const xAxis = d3.axisBottom(xScale);
	// use an exponent format for values like .01 or .1
	const xAxisForTinyValues = d3.axisBottom(xScale).ticks(10, 'e');

	// y scale for main chart
	const yScale = wrappedBand(true).paddingInner(0.1).paddingOuter(0.2);
	const yAxis = d3.axisLeft(yScale);

	function getXAxis(chartHasTinyValuesOnly) {
		if (chartHasTinyValuesOnly) return xAxisForTinyValues;
		return xAxis;
	}

	function init({ properties, extent }, { height, width }) {
		const chartHasTinyValuesOnly = extent[1] < 1;

		// create group for grid lines
		d3.select(svg).append('g').classed('gridGroup', true);

		// create group for bar labels
		d3.select(svg).append('g').classed('labelGroup', true);

		// create main chart bar group
		d3.select(svg).append('g').classed('barGroup', true);

		// create yAxis
		yScale.domain(properties).range([margin.top, height - margin.bottom]);
		d3.select(svg).append('g').classed('yAxis', true).attr('transform', `translate(${margin.left},0)`).call(yAxis);

		const maxText = measureAxisText(svg);

		// create xAxis
		xScale
			.domain([0, extent[1]])
			.range([getMarginLeft(width, maxText.width, true), width - margin.right])
			.nice();
		d3.select(svg)
			.append('g')
			.classed('xAxis', true)
			.attr('transform', `translate(0,${height - margin.bottom})`)
			.call(getXAxis(chartHasTinyValuesOnly));

		// re-set the yAxis transform
		d3.select(svg)
			.select('.yAxis')
			.attr('transform', `translate(${getMarginLeft(width, maxText.width, true)},0)`);
	}

	function update(
		{ data, properties, keys, extent, colorPropertyMap, dataHasLinks = false, alternateDataFormatter, hasLabelTooltips },
		{ height, width },
		{
			colorScale,
			showBarLabels = true,
			limitAxisLabels = true,
			maxLabelCharacters = 18,
			hyperLink
		},
		hideLabels
	) {
		let _margin = margin;
		if(hideLabels) {
			_margin = {
				left: 0, right: 0, top: 0, bottom: 0
			};
		}

		const getWidth = (d) => (isValid(d) ? xScale(d[1]) - xScale(d[0]) : 0);
		const chartHasTinyValuesOnly = extent[1] < 1;

		// update y axis
		yScale.domain(properties).range([_margin.top, height - _margin.bottom]);

		if (yScale.bandwidth() < 5) yAxis.tickValues(yScale.domain().filter((_, i) => !(i % 10)));
		else yAxis.tickValues(yScale.domain());

		const yAxisTextLabels = d3.select(svg)
			.select('g.yAxis')
			.attr('transform', `translate(${_margin.left},0)`)
			.call(yAxis)
			.selectAll('text')
			.text((currentText) => currentText
				/*!limitAxisLabels || currentText.length <= maxLabelCharacters
					? currentText
					: currentText.substring(0, maxLabelCharacters) + '...'*/
			)
			.attr('cy-test', 'stackbar-data-hbar-y-label')
			.each(fitLabelWidth(10, "'Libre_Franklin',ArialMT,sans-serif", (textLength) => textLength + 10 > (getMarginLeft(width - 30, 100000, limitAxisLabels))));

		if (dataHasLinks || hyperLink) {
			yAxisTextLabels
				.data(data)
				.attr('cursor', 'pointer')
				.on('mouseover', function(_, d) {
					if (hasLabelTooltips){	
						const x = xScale(d.value);
						const y = yScale(d.label) + _margin.top / 2;
						const { title, subtitle, count } = d.labelTooltip;

						const data = { title, subtitle, count};

						d3.select(svg).selectAll('.bar').transition().duration(100).style('opacity', 0.5);

						d3.select(this)
							.transition()
							.duration(100)
							.style('opacity', 1.0)
							.on('end', () => {
								updateTooltip && updateTooltip(null, null);
								updateTooltip && updateTooltip(
									data,
									{
										x: x,
										y: y,
									},
									{ position: Position.TOP_RIGHT }
								);
							})
						}
					}
				)
				.on('mouseout', function () {
					if (hasLabelTooltips){	
						d3.select(svg)
							.selectAll('.bar')
							.transition()
							.duration(100)
							.style('opacity', 1)
							.on('end', () => {
								updateTooltip && updateTooltip(null, null);
							});
						}
				})
				.on('click', function (event, {href, label}) {
					if (href) navigateTo('/' + href);
					//Following used in CyHy Curated Views Single Vul
					else if(hyperLink) {
						if(hyperLink.includes("/curated_views/Organization")){
							// Linking to an org is involves different steps than linking to a vuln.,
							// so we split the logic into two branches
							//HACK for now. May want to include the orgs key? Or make hyperLink a func? (dont love that idea bc it comes
							// thru widgetConfig, which should be hydratable
							// https://stackoverflow.com/a/47864833
							const key = (label).match(/\((.*)\)/).pop();
							if(key)
							{
								window.open(`${hyperLink.replace("${}", key)}`, '_blank');
							}
						}
						else{
							window.open(`${hyperLink.replace("${}", label)}`, '_blank');
						}
					}
				});
		}

		const maxText = measureAxisText(svg);

		// update x axis
		xScale
			.domain([0, extent[1]])
			.range([hideLabels ? 0 : getMarginLeft(width, maxText.width, limitAxisLabels), width - _margin.right])
			.nice();
		d3.select(svg)
			.select('g.xAxis')
			.attr('transform', `translate(0,${height - _margin.bottom})`)
			.call(getXAxis(chartHasTinyValuesOnly))
			.selectAll('text')
			.attr('cy-test', 'stackbar-data-hbar-x-label');

		// re-set axis
		d3.select(svg)
			.select('g.yAxis')
			.attr('transform', `translate(${hideLabels ? 0 : getMarginLeft(width, maxText.width, limitAxisLabels)},0)`);

		// add the X gridlines
		d3.select(svg).selectAll('.grid').remove();

		d3.select(svg)
			.select('.gridGroup')
			.append('g')
			.attr('class', 'grid y-grid')
			.attr('transform', `translate(0 ${height - _margin.bottom})`)
			.call(
				d3
					.axisTop(xScale)
					.tickSize(height - _margin.bottom - _margin.top)
					.tickFormat('')
			);

		// create the top labels
		d3.select(svg)
			.select('.labelGroup')
			.selectAll('.topLabel')
			.data(data)
			.join(
				(enter) => {
					return enter
						.append('text')
						.classed('topLabel', true)
						.attr('x', (d) => xScale(d.value) + 5)
						.attr('y', (d) => yScale(d.label) + yScale.bandwidth() / 2)
						.attr('font-size', 10)
						.attr('text-anchor', 'beginning')
						.attr('dominant-baseline', 'middle')
						.text((d) =>
							alternateDataFormatter ? alternateDataFormatter(d.value) : formatValueScientificNotation(d.value)
						)
						.attr('opacity', function () {
							const noFit = this.getBBox().height > yScale.bandwidth();
							return noFit ? 0 : 1;
						})
						.attr('cy-test', 'stackbar-data-hbar-toptext');
				},
				(update) => {
					return update
						.attr('x', (d) => xScale(d.value) + 5)
						.attr('y', (d) => yScale(d.label) + yScale.bandwidth() / 2)
						.text((d) =>
							alternateDataFormatter ? alternateDataFormatter(d.value) : formatValueScientificNotation(d.value)
						)
						.attr('opacity', function () {
							if(hideLabels)
								return 0;
							const noFit = this.getBBox().height > yScale.bandwidth();
							return noFit ? 0 : 1;
						});
				},
				(exit) => exit.remove()
			);

		// The stack call produces a dense matrix of values (n rows by m colums)
		// in the case where there are many unique values amongst the rows this
		// is extremely poorly performing for no real reason.  So we filter out invalid
		// values and move on.
		const stacked = d3.stack().keys(keys)(data);
		const filtered = stacked.map((bar) => {
			const retVal = bar.filter((segment) => {
				return !isNaN(segment[1]);
			});
			retVal.key = bar.key;
			retVal.index = bar.index;
			return retVal;
		});

		// primary bands
		d3.select(svg)
			.select('.barGroup')
			.selectAll('g.band')
			.data(filtered, (d) => d.key)
			.join(
				(enter) => enter.append('g').classed('band', true).attr('opacity', 1),
				(update) => update,
				(exit) => exit.remove()
			)
			.selectAll('g.bar')
			.data((d) => d)
			.join(
				(enter) => {
					const g = enter
						.append('g')
						.classed('bar', true)
						.attr('transform', (d) => `translate(0 ${yScale(d.data.label)})`)
						.attr('fill', function () {
							const key = d3.select(this.parentNode).datum().key;
							return colorScale(colorPropertyMap[key]);
						})
						.attr('cy-test', 'stackbar-data-hbar')
						.on('mouseover', function (_, d) {
							const key = d3.select(this.parentNode).datum().key;
							const x = xScale(d.data[key]);
							const y = yScale(d.data.label) + _margin.top / 2;

							const data = {
								title: d.data.label,
								subtitle: key,
								count: alternateDataFormatter ? alternateDataFormatter(d.data[key]) : d.data[key],
							};
							d3.select(svg).selectAll('.bar').transition().duration(100).style('opacity', 0.5);

							d3.select(this)
								.transition()
								.duration(100)
								.style('opacity', 1.0)
								.on('end', () => {
									updateTooltip && updateTooltip(null, null);

									updateTooltip &&
										updateTooltip(
											data,
											{
												x: x,
												y: y,
											},
											{ position: Position.TOP_RIGHT }
										);
								});
						})
						.on('mouseout', function () {
							d3.select(svg)
								.selectAll('.bar')
								.transition()
								.duration(100)
								.style('opacity', 1)
								.on('end', () => {
									updateTooltip && updateTooltip(null, null);
								});
						})
						.on('click', function (event, {data: {href, label}}) {
							if (href) navigateTo('/' + href);
							//Following used in CyHy Curated Views Single Vul
							else if(hyperLink) {
								if(hyperLink.includes("/curated_views/Organization")){
									// Linking to an org is involves different steps than linking to a vuln.,
									// so we split the logic into two branches
									//HACK for now. May want to include the orgs key? Or make hyperLink a func? (dont love that idea bc it comes
									// thru widgetConfig, which should be hydratable
									// https://stackoverflow.com/a/47864833
									const key = (label).match(/\((.*)\)/).pop();
									if(key)
									{
										window.open(`${hyperLink.replace("${}", key)}`, '_blank');
									}
								}
								else{
									window.open(`${hyperLink.replace("${}", label)}`, '_blank');
								}

							}
						});

					if (dataHasLinks) {
						g.append('svg:a')
							.attr('xlink:href', function (d) {
								return d.data.publicURL + d.data.href;
							})
							.attr('fill', function () {
								const parentBar = this.parentNode;
								const fill = d3.select(parentBar).attr('fill');
								return fill;
							})
							.append('rect')
							.transition()
							.duration(150)
							.attr('opacity', 1)
							.attr('x', (d) => xScale(d[0]))
							.attr('y', 0)
							.attr('width', (d) => getWidth(d))
							.attr('height', yScale.bandwidth());
					} else {
						g.append('rect')
							.transition()
							.duration(150)
							.attr('opacity', 1)
							.attr('x', (d) => xScale(d[0]))
							.attr('y', 0)
							.attr('width', (d) => getWidth(d))
							.attr('height', yScale.bandwidth());
					}

					// display labels inside of the bars
					// visibility controlled by *showBarLabels*
					/*g.append('text')
						.classed('barLabel', true)
						.attr('text-anchor', 'middle')
						.attr('dominant-baseline', 'middle')
						.attr('font-size', 10)
						.attr('pointer-events', dataHasLinks ? 'none' : 'visiblePainted')
						.attr('y', () => yScale.bandwidth() / 2)
						.attr('x', (d) => (isValid(d) ? xScale(d[0]) + getWidth(d) / 2 : 0))
						.attr('fill', function () {
							const bar = this.parentNode;
							const fill = d3.select(bar).attr('fill');
							return fill ? readableColor(fill) : 'black';
						})
						.text(function (d) {
							const bar = this.parentNode;
							const group = bar.parentNode;
							const key = d3.select(group).datum().key;
							return alternateDataFormatter
								? alternateDataFormatter(d.data[key])
								: formatValueScientificNotation(d.data[key]);
						})
						.attr('opacity', function (d) {
							const noFit =
								this.getBBox().height > yScale.bandwidth() || this.getComputedTextLength() + 8 > getWidth(d);
							return noFit ? 0 : showBarLabels ? 1 : 0;
						})
						.attr('cy-test', 'stackbar-data-hbar-text');*/

					return g;
				},
				(update) => {
					update
						.transition()
						.duration(100)
						.attr('transform', (d) => `translate(0 ${yScale(d.data.label)})`)
						.attr('fill', function () {
							const key = d3.select(this.parentNode).datum().key;
							return colorScale(colorPropertyMap[key]);
						});

					update
						.select('rect')
						.transition()
						.duration(100)
						.attr('opacity', 1)
						.attr('x', (d) => xScale(d[0]))
						.attr('y', 0)
						.attr('width', (d) => getWidth(d))
						.attr('height', yScale.bandwidth());

					update
						.select('.barLabel')
						.transition()
						.duration(100)
						.attr('y', () => yScale.bandwidth() / 2)
						.attr('x', (d) => (isValid(d) ? xScale(d[0]) + getWidth(d) / 2 : 0))
						.attr('fill', function () {
							const bar = this.parentNode;
							const fill = d3.select(bar).attr('fill');
							return fill ? readableColor(fill) : 'black';
						})
						.text(function (d) {
							const bar = this.parentNode;
							const group = bar.parentNode;
							const key = d3.select(group).datum().key;
							return alternateDataFormatter
								? alternateDataFormatter(d.data[key])
								: formatValueScientificNotation(d.data[key]);
						})
						.attr('opacity', function (d) {
							if(hideLabels)
								return 0;
							const noFit =
								this.getBBox().height > yScale.bandwidth() || this.getComputedTextLength() + 8 > getWidth(d);
							return noFit ? 0 : showBarLabels ? 1 : 0;
						});
					return update;
				},
				(exit) => exit.remove()
			);
	}

	function mouseOver(event, item) {
		onMouseOver(svg, event, item);
	}

	function mouseOut() {
		onMouseOut(svg);
	}

	function clear() {
		onClear(svg);
	}

	function externalUpdate({ data, dimensions, options, hideLabels }) {
		if (!initialized) {
			init(data, dimensions);
			initialized = true;
		}
		update(data, dimensions, options, hideLabels);
	}

	return {
		update: externalUpdate,
		clear: clear,
		mouseOver: mouseOver,
		mouseOut: mouseOut,
	};
};
